import { TypographyOptions } from "@mui/material/styles/createTypography";
import React from "react";

const FONT_WEIGHT_LIGHT = 300;
const FONT_WEIGHT_REGULAR = 400;
const FONT_WEIGHT_SEMI_BOLD = 600;
const FONT_WEIGHT_BOLD = 700;

type LxTypographyVariants =
  | "lxPageTitle"
  | "lxDialogTitle"
  | "lxSectionTitle"
  | "lxWidgetTitle"
  | "lxCardTitle"
  | "lxSubtitle1"
  | "lxSubtitle2"
  | "lxFieldsetTitle"
  | "lxBody1"
  | "lxBody2"
  | "lxBody3"
  | "lxCaption"
  | "lxOverline"
  | "lxButtonLarge"
  | "lxButtonMedium"
  | "lxButtonSmall"
  | "lxAvatarLetter"
  | "lxInputLabel"
  | "lxHelperText"
  | "lxInputInputText"
  | "lxTooltip";

declare module "@mui/material/styles" {
  interface TypographyVariants {
    lxPageTitle?: React.CSSProperties;
    lxDialogTitle?: React.CSSProperties;
    lxSectionTitle?: React.CSSProperties;
    lxWidgetTitle?: React.CSSProperties;
    lxCardTitle?: React.CSSProperties;
    lxSubtitle1?: React.CSSProperties;
    lxSubtitle2?: React.CSSProperties;
    lxFieldsetTitle?: React.CSSProperties;
    lxBody1?: React.CSSProperties;
    lxBody2?: React.CSSProperties;
    lxBody3?: React.CSSProperties;
    lxCaption?: React.CSSProperties;
    lxOverline?: React.CSSProperties;
    lxButtonLarge?: React.CSSProperties;
    lxButtonMedium?: React.CSSProperties;
    lxButtonSmall?: React.CSSProperties;
    lxAvatarLetter?: React.CSSProperties;
    lxInputLabel?: React.CSSProperties;
    lxHelperText?: React.CSSProperties;
    lxInputInputText?: React.CSSProperties;
    lxTooltip?: React.CSSProperties;
  }

  // allow configuration using `createTheme`
  interface TypographyVariantsOptions {
    lxPageTitle?: React.CSSProperties;
    lxDialogTitle?: React.CSSProperties;
    lxSectionTitle?: React.CSSProperties;
    lxWidgetTitle?: React.CSSProperties;
    lxCardTitle?: React.CSSProperties;
    lxSubtitle1?: React.CSSProperties;
    lxSubtitle2?: React.CSSProperties;
    lxFieldsetTitle?: React.CSSProperties;
    lxBody1?: React.CSSProperties;
    lxBody2?: React.CSSProperties;
    lxBody3?: React.CSSProperties;
    lxCaption?: React.CSSProperties;
    lxOverline?: React.CSSProperties;
    lxButtonLarge?: React.CSSProperties;
    lxButtonMedium?: React.CSSProperties;
    lxButtonSmall?: React.CSSProperties;
    lxAvatarLetter?: React.CSSProperties;
    lxInputLabel?: React.CSSProperties;
    lxHelperText?: React.CSSProperties;
    lxInputInputText?: React.CSSProperties;
    lxTooltip?: React.CSSProperties;
  }
}

// Update the Typography's variant prop options
declare module "@mui/material/Typography" {
  interface TypographyPropsVariantOverrides {
    lxPageTitle: true;
    lxDialogTitle: true;
    lxSectionTitle: true;
    lxWidgetTitle: true;
    lxCardTitle: true;
    lxSubtitle1: true;
    lxSubtitle2: true;
    lxFieldsetTitle: true;
    lxBody1: true;
    lxBody2: true;
    lxBody3: true;
    lxCaption: true;
    lxOverline: true;
    lxButtonLarge: true;
    lxButtonMedium: true;
    lxButtonSmall: true;
    lxAvatarLetter: true;
    lxInputLabel: true;
    lxHelperText: true;
    lxInputInputText: true;
    lxTooltip: true;
  }
}

export const lxTypography: {
  [k in LxTypographyVariants]: React.CSSProperties;
} = {
  lxPageTitle: {
    fontSize: pxToRem(32),
    lineHeight: percent(120),
    fontWeight: FONT_WEIGHT_SEMI_BOLD,
    letterSpacing: pxToRem(-0.25),
  },
  lxDialogTitle: {
    fontSize: pxToRem(22),
    lineHeight: percent(150),
    fontWeight: FONT_WEIGHT_SEMI_BOLD,
    letterSpacing: pxToRem(-0.25),
  },
  lxSectionTitle: {
    fontSize: pxToRem(22),
    lineHeight: percent(150),
    fontWeight: FONT_WEIGHT_SEMI_BOLD,
    letterSpacing: pxToRem(-0.25),
  },
  lxWidgetTitle: {
    fontSize: pxToRem(22),
    lineHeight: percent(150),
    fontWeight: FONT_WEIGHT_SEMI_BOLD,
    letterSpacing: pxToRem(-0.25),
  },
  lxCardTitle: {
    fontSize: pxToRem(22),
    lineHeight: percent(150),
    fontWeight: FONT_WEIGHT_SEMI_BOLD,
    letterSpacing: pxToRem(-0.25),
  },
  lxSubtitle1: {
    fontSize: pxToRem(18),
    lineHeight: percent(160),
    fontWeight: FONT_WEIGHT_SEMI_BOLD,
    letterSpacing: pxToRem(-0.25),
  },
  lxSubtitle2: {
    fontSize: pxToRem(14),
    lineHeight: percent(160),
    fontWeight: FONT_WEIGHT_SEMI_BOLD,
    letterSpacing: pxToRem(0.12),
  },
  lxFieldsetTitle: {
    fontSize: pxToRem(18),
    lineHeight: percent(160),
    fontWeight: FONT_WEIGHT_SEMI_BOLD,
    letterSpacing: pxToRem(-0.25),
  },
  lxBody1: {
    fontSize: pxToRem(16),
    lineHeight: percent(150),
    fontWeight: FONT_WEIGHT_REGULAR,
    letterSpacing: pxToRem(0.15),
  },
  lxBody2: {
    fontSize: pxToRem(14),
    lineHeight: percent(150),
    fontWeight: FONT_WEIGHT_REGULAR,
    letterSpacing: pxToRem(0.15),
  },
  lxBody3: {
    fontSize: pxToRem(12),
    lineHeight: percent(140),
    fontWeight: FONT_WEIGHT_REGULAR,
    letterSpacing: pxToRem(0.15),
  },
  lxCaption: {
    fontSize: pxToRem(12),
    lineHeight: percent(166),
    fontWeight: FONT_WEIGHT_REGULAR,
    letterSpacing: pxToRem(0.53),
  },
  lxOverline: {
    fontSize: pxToRem(12),
    lineHeight: percent(266),
    fontWeight: FONT_WEIGHT_REGULAR,
    letterSpacing: pxToRem(1.33),
    textTransform: "uppercase",
  },
  lxButtonLarge: {
    fontSize: pxToRem(15),
    lineHeight: percent(173.3),
    fontWeight: FONT_WEIGHT_SEMI_BOLD,
    letterSpacing: percent(173.3),
    textTransform: "uppercase",
  },
  lxButtonMedium: {
    fontSize: pxToRem(14),
    lineHeight: percent(171.4),
    fontWeight: FONT_WEIGHT_SEMI_BOLD,
    letterSpacing: percent(171.4),
    textTransform: "uppercase",
  },
  lxButtonSmall: {
    fontSize: pxToRem(13),
    lineHeight: percent(169.2),
    fontWeight: FONT_WEIGHT_REGULAR,
    letterSpacing: percent(169.2),
  },
  lxAvatarLetter: {
    fontSize: pxToRem(20),
    lineHeight: percent(100),
    fontWeight: FONT_WEIGHT_REGULAR,
    letterSpacing: percent(100),
  },
  lxInputLabel: {
    fontSize: pxToRem(12),
    lineHeight: percent(150),
    fontWeight: FONT_WEIGHT_REGULAR,
    letterSpacing: percent(100),
  },
  lxHelperText: {
    fontSize: pxToRem(12),
    lineHeight: percent(166.6),
    fontWeight: FONT_WEIGHT_REGULAR,
    letterSpacing: percent(166.6),
  },
  lxInputInputText: {
    fontSize: pxToRem(16),
    lineHeight: percent(118.8),
    fontWeight: FONT_WEIGHT_REGULAR,
    letterSpacing: percent(118.8),
  },
  lxTooltip: {
    fontSize: pxToRem(10),
    lineHeight: percent(140),
    fontWeight: FONT_WEIGHT_SEMI_BOLD,
    letterSpacing: percent(140),
  },
};

// Override Typography (only marked values customized) -----------------------------
export const typography: TypographyOptions = {
  // Basic Settings
  htmlFontSize: 16,
  fontFamily: '"Open Sans", Helvetica, Arial, sans-serif;',
  fontSize: 14,
  fontWeightLight: FONT_WEIGHT_LIGHT,
  fontWeightRegular: FONT_WEIGHT_REGULAR,
  fontWeightMedium: FONT_WEIGHT_SEMI_BOLD,
  fontWeightBold: FONT_WEIGHT_BOLD,

  // Headings (all hx Headings customized) -----------------------------
  // TypeScale Major Second rounded (1.125)
  h1: {
    fontSize: "2rem",
    fontWeight: FONT_WEIGHT_SEMI_BOLD,
    letterSpacing: "-0.01562em",
  },
  h2: {
    fontSize: "1.813rem",
    fontWeight: FONT_WEIGHT_REGULAR,
    letterSpacing: "0.00833em",
  },
  h3: {
    fontSize: "1.625rem",
    fontWeight: FONT_WEIGHT_REGULAR,
    letterSpacing: "0em",
  },
  h4: {
    fontSize: "1.438rem",
    fontWeight: FONT_WEIGHT_REGULAR,
    letterSpacing: "0.0.00735em",
  },
  h5: {
    fontSize: "1.25rem",
    fontWeight: FONT_WEIGHT_REGULAR,
    letterSpacing: "0em",
  },
  h6: {
    fontSize: "1.125rem",
    fontWeight: FONT_WEIGHT_SEMI_BOLD,
    letterSpacing: "0.0075em",
  },

  // Subheadings (only marked values customized) -----------------------------
  subtitle1: lxTypography.lxSubtitle1 as any,
  subtitle2: lxTypography.lxSubtitle2 as any,
  body1: lxTypography.lxBody1 as any,
  body2: lxTypography.lxBody2 as any,
  button: lxTypography.lxButtonMedium as any,
};

function pxToRem(px: number) {
  return `${px / 16}rem`;
}

function percent(percent: number) {
  return `${percent}%`;
}
